import React, { useState } from "react";
import {
  Card,
  Typography,
  Tag,
  Button,
  Row,
  Col,
  Statistic,
  Tooltip,
  Space,
  Divider,
  Flex,
  ConfigProvider,
} from "antd";
import {
  EditOutlined,
  InfoCircleFilled,
  InfoCircleOutlined,
  SoundOutlined,
} from "@ant-design/icons";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import TemplateInsightsModelSpeaker from "../../../../../assets/customSVG/template/TemplateInsightsModelSpeaker";
import TemplatePreviewModal from "../../../page/whatsapp/messageTemplate/components/TemplatePreviewModal";
import {
  templateCategory,
  templateLibraryData,
} from "../../../constant/templateLibrary";
import { templateLanguages } from "../../../constant/template";
import TemplatePreview from "../../../page/whatsapp/messageTemplate/components/TemplatePreview";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { updateTemplateDetails } from "../../../../../reduxToolkit/templateSlice";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ChartTitle,
  ChartTooltip,
  Legend,
);

const { Title, Text } = Typography;

const message_insights = [
  {
    title: "Message Sent",
    key: "message_sent_count",
  },
  {
    title: "Message Delivered",
    key: "delivered_count",
  },
  {
    title: "Message Opened",
    key: "read_count",
  },
  {
    title: "Message Bounced",
    key: "bounced_count",
  },
];

const chartOptions = {
  responsive: true,
  // maintainAspectRatio: false,

  plugins: {
    legend: {
      position: "bottom",
      labels: {
        usePointStyle: true,
        pointStyle: "circle",
        padding: 20,
        boxWidth: 8,
        boxHeight: 8,
      },
    },
    tooltip: {
      mode: "index",
      position: "nearest",

      intersect: false,
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { font: { size: 10 } },
    },
    y: {
      grid: { color: "#f0f0f0" },
      ticks: { font: { size: 10 } },
    },
  },
};

const TemplateInfoAndInsights = ({
  performanceData = {},
  selectedTemplate,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);

  const closeModal = () => {
    setModalOpen(false);
  };

  // dynamic chart data from performanceData prop
  const dateLabels = Object.keys(performanceData).sort();
  const sentData = dateLabels.map(
    (date) => performanceData[date].message_sent_count || 0,
  );
  const deliveredData = dateLabels.map(
    (date) => performanceData[date].delivered_count || 0,
  );
  const openData = dateLabels.map(
    (date) => performanceData[date].read_count || 0,
  );
  const bounceData = dateLabels.map(
    (date) => performanceData[date].bounced_count || 0,
  );
  const chartDataDynamic = {
    labels: dateLabels.map((date) => {
      // Convert date string to ISO format
      const dateObj = new Date(date);
      return moment(dateObj).format("DD MMM");
    }),
    datasets: [
      {
        label: "Messages Sent",
        data: sentData,
        borderColor: "#a259ff",
        backgroundColor: "rgba(162,89,255,0.1)",
        tension: 0.4,
        pointRadius: 3,
        fill: false,
      },
      {
        label: "Messages Delivered",
        data: deliveredData,
        borderColor: "#00c48c",
        backgroundColor: "rgba(0,196,140,0.1)",
        tension: 0.4,
        pointRadius: 3,
        fill: false,
      },
      {
        label: "Messages Open",
        data: openData,
        borderColor: "#3b82f6",
        backgroundColor: "rgba(59,130,246,0.1)",
        tension: 0.4,
        pointRadius: 3,
        fill: false,
      },
      {
        label: "Messages Bounced",
        data: bounceData,
        borderColor: "#ff4d4f",
        backgroundColor: "rgba(255,77,79,0.1)",
        tension: 0.4,
        pointRadius: 3,
        fill: false,
      },
    ],
  };
  const totalSent = sentData.reduce((sum, v) => sum + v, 0);
  const totalDelivered = deliveredData.reduce((sum, v) => sum + v, 0);
  const totalOpen = openData.reduce((sum, v) => sum + v, 0);
  const insights_values = {
    message_sent_count: totalSent,
    delivered_count: totalDelivered,
    read_count: totalOpen,
    bounced_count: bounceData.reduce((sum, v) => sum + v, 0),
  };

  return (
    <div className="rounded-lg">
      {/* heading section of the insights */}
      <Row
        align="middle"
        justify="space-between"
        className="my-3 px-3 py-1 bg-white rounded-lg"
      >
        <Col className="flex items-center">
          <Flex
            align="center"
            justify="center"
            className="bg-[#05805C] size-12 rounded-lg mr-4"
          >
            <TemplateInsightsModelSpeaker />
          </Flex>
          <Flex vertical>
            <Flex gap={14}>
              <Title
                level={5}
                className="m-0 max-w-[250px] overflow-hidden whitespace-nowrap text-ellipsis"
                style={{ marginBottom: 0 }}
              >
                {selectedTemplate?.name}
              </Title>

              <Flex
                align="center"
                justify="center"
                className="bg-[#e6fff2] border-[1px] border-[#00c48c] font-medium px-2 rounded-md"
              >
                <Text className="text-[#00c48c] text-xs">Active</Text>
              </Flex>
            </Flex>
            <Flex className="mt-2" align="center" gap={10}>
              <Flex
                align="center"
                justify="center"
                className="bg-[#f0f0f0] px-2 py-1 rounded-sm"
              >
                <Text className="text-[#737373] text-xs">
                  {selectedTemplate?.category}
                </Text>
              </Flex>

              <Divider
                type="vertical"
                variant="solid"
                style={{
                  margin: 0,
                  borderInlineStartColor: "#898E99",
                }}
              />

              <Flex
                align="center"
                justify="center"
                className="bg-[#f0f0f0] px-2 py-1 rounded-sm"
              >
                <Text className="text-[#737373] text-xs">
                  {selectedTemplate?.language}
                </Text>
              </Flex>

              <Divider
                type="vertical"
                variant="solid"
                style={{
                  margin: 0,
                  borderInlineStartColor: "#898E99",
                }}
              />

              <Text type="secondary" className="text-xs ml-1">
                Created @{" "}
                {moment(selectedTemplate?.created_at).format("MMM, DD YYYY")}
              </Text>
            </Flex>
          </Flex>
        </Col>
        <Col>
          <ConfigProvider
            theme={{
              components: {
                Button: {
                  defaultBg: "#05805C",
                  defaultColor: "#fff",
                  defaultHoverBg: "#fff",
                  defaultHoverColor: "#05805C",
                  defaultHoverBorderColor: "#05805C",
                  defaultActiveColor: "#05805C",
                  defaultActiveBg: "#fff",
                  defaultActiveBorderColor: "#05805C",
                },
              },
            }}
          >
            <Button
              disabled={selectedTemplate.status !== "APPROVED"}
              onClick={() => {
                navigate(
                  "/user/channels/whatsapp/duplicate-template/template-details",
                );
                dispatch(updateTemplateDetails({ data: selectedTemplate }));
              }}
              className="px-4 py-[1rem]"
            >
              Edit Template
            </Button>
          </ConfigProvider>
        </Col>
      </Row>

      {/* main content */}
      <Divider style={{ margin: "12px 0" }} />
      <Row gutter={12}>
        {/* Left: Template Preview Card */}
        <Col xs={24} md={8} lg={7}>
          {/* {previewCard} */}
          <div className="bg-white rounded-lg p-1 pt-5">
            <Text className="ps-4 font-semibold" strong>
              Preview
            </Text>
            <Divider style={{ margin: 0, marginBlock: 5 }} />
            <TemplatePreview
              isOpen={modalOpen}
              onClose={closeModal}
              selectedTemplate={selectedTemplate}
            />
          </div>
        </Col>

        {/* Right: Insights and Performance (stacked vertically) */}
        <Col xs={24} md={16} lg={17}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Analytics Insights */}
            {/* comments as it is not required by abinesh */}
            {/* <Row gutter={[12, 12]}>
              {stats.map((stat) => (
                <Col xs={12} md={6} key={stat.title}>
                  <div className="p-4 rounded-lg bg-white h-[100px] flex flex-col justify-between w-full shadow-sm">
                    <div className>
                      <Text
                        type="secondary"
                        className="text-xs me-2 font-medium"
                      >
                        {stat.title}
                      </Text>
                      <Tooltip title={stat.tooltip}>
                        <InfoCircleFilled className="text-[13px] text-gray-400" />
                      </Tooltip>
                    </div>
                    <div className="text-lg font-semibold">{stat.value}</div>
                  </div>
                </Col>
              ))}
            </Row> */}

            <Row gutter={[12, 12]}>
              {message_insights.map((stat) => (
                <Col xs={12} md={6} key={stat.title}>
                  <div className="p-4 rounded-lg bg-white h-[100px] flex flex-col justify-between w-full items-center shadow-sm">
                    <div>
                      <Text
                        type="secondary"
                        className="text-xs me-2 font-medium"
                      >
                        {stat.title}
                      </Text>
                    </div>
                    <Text className="text-lg font-semibold">
                      {insights_values[stat.key]}
                    </Text>
                  </div>
                </Col>
              ))}
            </Row>

            {/* Template Performance Section */}
            <Card
              style={{ borderRadius: 12 }}
              styles={{ body: { padding: 24 } }}
            >
              <Title level={5} style={{ marginBottom: 16 }}>
                Performance
              </Title>
              <Divider style={{ margin: "12px 0" }} />

              <Line
                data={chartDataDynamic}
                options={chartOptions}
                height={155}
              />

              {/* commented as discussed with abinesh */}
              {/* <Row gutter={24} style={{ marginTop: 24 }}>
                <Col
                  span={8}
                  className="flex flex-col justify-center align-center"
                >
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: "#a259ff",
                        marginRight: 6,
                        verticalAlign: "middle",
                      }}
                    />
                    Messages sent
                  </Text>
                  <div style={{ fontWeight: 600, fontSize: 20, marginTop: 4 }}>
                    {totalSent}
                  </div>
                </Col>
                <Col
                  span={8}
                  className="flex flex-col justify-center align-center"
                >
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: "#00c48c",
                        marginRight: 6,
                        verticalAlign: "middle",
                      }}
                    />
                    Messages Delivered{" "}
                    <Tooltip title="Messages delivered">
                      <InfoCircleOutlined
                        style={{ fontSize: 13, color: "#bfbfbf" }}
                      />
                    </Tooltip>
                  </Text>
                  <div style={{ fontWeight: 600, fontSize: 20, marginTop: 4 }}>
                    {totalDelivered}
                  </div>
                </Col>
                <Col
                  span={8}
                  className="flex flex-col justify-center align-center"
                >
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: "#3b82f6",
                        marginRight: 6,
                        verticalAlign: "middle",
                      }}
                    />
                    Messages Open
                  </Text>
                  <div style={{ fontWeight: 600, fontSize: 20, marginTop: 4 }}>
                    {totalOpen}
                  </div>
                </Col>
              </Row> */}
            </Card>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default TemplateInfoAndInsights;
